import { ColumnHelper } from "@tanstack/react-table";
import { Template } from "PFApp/booking/types/manage_reports";
import { LoadingDots } from "PFComponents/loading_dots";
import Table from "PFComponents/table";
import { sortingDateFn } from "PFComponents/table/table.utils";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useExportWizardModalContext } from "../../../export_wizard";
import css from "../../manage_reports.module.scss";
import { DateColumn } from "../column/date_column";
import { TemplateColumn } from "../column/template_column";
import { AddNewExportReport } from "../components/add_new_export_report";

enum ColumnId {
  TemplateName = "templateNameColumn",
  CreationDate = "creationDateColumn",
  ReportsUsing = "reportsUsing"
}

type TemplatesTabProps = {
  templates: Template[];
  isLoading: boolean;
  onClose: () => void;
};

const TemplatesTab = ({ templates, isLoading, onClose }: TemplatesTabProps): JSX.Element => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "reports.manageReports.templates.table" });
  const { open } = useExportWizardModalContext();
  const handleGenerateColumns = useCallback(
    (columnHelper: ColumnHelper<Template>) => [
      columnHelper.accessor((row) => row.name, {
        id: ColumnId.TemplateName,
        cell: (info) => <TemplateColumn templateName={info.getValue()} />,
        header: () => <span className={css.tableHeader}>{t("templateColumn")}</span>
      }),
      columnHelper.accessor((row) => row.createdAt, {
        id: ColumnId.CreationDate,
        cell: (info) => <DateColumn date={info.getValue()} />,
        header: () => <span className={css.tableHeader}>{t("creationDateColumn")}</span>,
        sortingFn: (rowA, rowB) =>
          sortingDateFn(new Date(rowA.original?.createdAt), new Date(rowB.original?.createdAt))
      }),
      columnHelper.accessor((row) => row.reportsUsing, {
        id: ColumnId.ReportsUsing,
        cell: (info) => <span>{info.getValue()}</span>,
        header: () => <span className={css.tableHeader}>{t("reportsUsingColumn")}</span>
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleRowClick = (template: Template) => {
    onClose();
    open({ currentTab: "fields", selectedTemplate: template });
  };

  return (
    <div className={css.rootTab}>
      <div>
        <AddNewExportReport />
      </div>
      {isLoading && <LoadingDots circlesEnabled />}
      {!isLoading && (
        <Table<Template>
          tableData={templates}
          generateColumns={handleGenerateColumns}
          simplePaginationEnabled
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
};

export default TemplatesTab;
